var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { shouldCacheResponse } from '../util/cache-predicate';
import { Header } from '../util/headers';
import { updateCache } from '../util/update-cache';
import { setupCacheData } from './util';
export class CacheResponseInterceptor {
    constructor(axios) {
        this.axios = axios;
        this.use = () => {
            this.axios.interceptors.response.use(this.onFulfilled);
        };
        this.onFulfilled = (axiosResponse) => __awaiter(this, void 0, void 0, function* () {
            const response = this.cachedResponse(axiosResponse);
            // Response is already cached
            if (response.cached) {
                return response;
            }
            // Skip cache
            // either false or weird behavior, config.cache should always exists, from global config merge at least
            if (!response.config.cache) {
                return Object.assign(Object.assign({}, response), { cached: false });
            }
            const cacheConfig = response.config.cache;
            const cache = yield this.axios.storage.get(response.id);
            if (
            // If the request interceptor had a problem
            cache.state === 'stale' ||
                cache.state === 'empty' ||
                // Should not hit here because of previous response.cached check
                cache.state === 'cached') {
                return response;
            }
            // Config told that this response should be cached.
            if (
            // For 'loading' values (post stale), this check was already run in the past.
            !cache.data &&
                !shouldCacheResponse(response, cacheConfig)) {
                yield this.rejectResponse(response.id);
                return response;
            }
            // avoid remnant headers from remote server to break implementation
            delete response.headers[Header.XAxiosCacheEtag];
            delete response.headers[Header.XAxiosCacheLastModified];
            if (cacheConfig.etag && cacheConfig.etag !== true) {
                response.headers[Header.XAxiosCacheEtag] = cacheConfig.etag;
            }
            if (cacheConfig.modifiedSince) {
                response.headers[Header.XAxiosCacheLastModified] =
                    cacheConfig.modifiedSince === true
                        ? 'use-cache-timestamp'
                        : cacheConfig.modifiedSince.toUTCString();
            }
            let ttl = cacheConfig.ttl || -1; // always set from global config
            if (cacheConfig === null || cacheConfig === void 0 ? void 0 : cacheConfig.interpretHeader) {
                const expirationTime = this.axios.headerInterpreter(response.headers);
                // Cache should not be used
                if (expirationTime === 'dont cache') {
                    yield this.rejectResponse(response.id);
                    return response;
                }
                ttl = expirationTime === 'not enough headers' ? ttl : expirationTime;
            }
            const data = setupCacheData(response, cache.data);
            if (typeof ttl === 'function') {
                ttl = yield ttl(response);
            }
            const newCache = {
                state: 'cached',
                ttl,
                createdAt: Date.now(),
                data
            };
            // Update other entries before updating himself
            if (cacheConfig === null || cacheConfig === void 0 ? void 0 : cacheConfig.update) {
                updateCache(this.axios.storage, response, cacheConfig.update);
            }
            const deferred = this.axios.waiting[response.id];
            // Resolve all other requests waiting for this response
            yield (deferred === null || deferred === void 0 ? void 0 : deferred.resolve(newCache.data));
            delete this.axios.waiting[response.id];
            // Define this key as cache on the storage
            yield this.axios.storage.set(response.id, newCache);
            // Return the response with cached as false, because it was not cached at all
            return response;
        });
        /** Rejects cache for this response. Also update the waiting list for this key by rejecting it. */
        this.rejectResponse = (key) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            // Update the cache to empty to prevent infinite loading state
            yield this.axios.storage.remove(key);
            // Reject the deferred if present
            (_a = this.axios.waiting[key]) === null || _a === void 0 ? void 0 : _a.reject(null);
            delete this.axios.waiting[key];
        });
        this.cachedResponse = (response) => {
            return Object.assign({ id: this.axios.generateKey(response.config), 
                // The request interceptor response.cache will return true or undefined. And true only when the response was cached.
                cached: response.cached || false }, response);
        };
    }
}
